// src/components/Modals/ReturnAssetModal.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, AppBar, Toolbar, IconButton, Typography, Box, Button,
  Container, Card, CardContent, Stack, TextField,
  CircularProgress, Snackbar, Alert, Chip, Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Slide } from '@mui/material';
import CloseIcon           from '@mui/icons-material/Close';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import HistoryIcon         from '@mui/icons-material/History';
import ErrorOutlineIcon    from '@mui/icons-material/ErrorOutline';
import QrCode2Icon         from '@mui/icons-material/QrCode2';
import BusinessIcon        from '@mui/icons-material/Business';

import { assetService } from "../../services/assetService";

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Gets local current timezone date as string formatted YYYY-MM-DD
 */
const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Avoids UTC timezone conversion shifts for simple display values
 */
const parseDateSafely = (val) => {
  if (!val) return null;
  if (typeof val?.toDate === 'function') return val.toDate();
  if (val?.seconds !== undefined) return new Date(val.seconds * 1000);

  // If simple date string YYYY-MM-DD, parse as local calendar day
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const [year, month, day] = val.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (val) => {
  const d = parseDateSafely(val);
  if (!d) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const formatDateTime = (val) => {
  const d = parseDateSafely(val);
  if (!d) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const getInitials = (name = '') => {
  if (!name || typeof name !== 'string') return '??';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  return parts.map((w) => w[0]).join('').toUpperCase().slice(0, 2);
};

// ─────────────────────────────────────────────────────────────────────────────
// SLIDE TRANSITION
// ─────────────────────────────────────────────────────────────────────────────

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─────────────────────────────────────────────────────────────────────────────
// STYLED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: '1px solid #e2e8f0',
}));

const SectionTitle = styled(Typography)(() => ({
  fontSize: '0.9375rem', fontWeight: 700, color: '#1e293b',
  marginBottom: '1.25rem', paddingBottom: '0.75rem',
  borderBottom: '1px solid #e5e7eb',
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: '0.8125rem', fontWeight: 600, color: '#374151',
  textTransform: 'uppercase', letterSpacing: '0.025em',
  marginBottom: '0.375rem',
}));

const FormGroup = styled(Box)(() => ({
  flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column',
  gap: '0.375rem',
  '& .MuiOutlinedInput-root': {
    background: '#fff',
    '& fieldset':        { borderColor: '#d1d5db' },
    '&:hover fieldset':  { borderColor: '#3b82f6' },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
      boxShadow:   '0 0 0 3px rgba(59,130,246,0.12)',
    },
  },
}));

const FormRow = styled(Box)(() => ({
  display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap',
  '&:last-child': { marginBottom: 0 },
}));

const HistoryEntry = styled(Box)(() => ({
  display: 'flex', gap: '1rem', alignItems: 'flex-start',
  padding: '1rem', borderRadius: '8px',
  background: '#fff7ed', border: '1px solid #fdba74',
}));

const cancelBtnSx = {
  px: 3, py: 1, backgroundColor: '#f3f4f6', color: '#374151',
  border: '1px solid #d1d5db', borderRadius: '6px', fontWeight: 500,
  textTransform: 'none', minWidth: '100px', height: '40px',
  '&:hover':    { backgroundColor: '#e5e7eb' },
  '&:disabled': { opacity: 0.6 },
};

const submitBtnSx = {
  px: 3, py: 1, backgroundColor: '#d97706', color: '#fff',
  borderRadius: '6px', fontWeight: 600, textTransform: 'none',
  minWidth: '160px', height: '40px',
  display: 'flex', alignItems: 'center', gap: 1,
  '&:hover':    { backgroundColor: '#b45309' },
  '&:disabled': { backgroundColor: '#fcd34d' },
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const ReturnAssetModal = ({
  open,
  asset       = null,
  onClose,
  onSave,
  onError,
  currentUser,
}) => {
  // ── Form state ────────────────────────────────────────────────────────────
  const [returnDate, setReturnDate] = useState(getLocalDateString());
  const [returnRemarks, setReturnRemarks] = useState('');
  const [remarksTouched, setRemarksTouched] = useState(false);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [loading,        setLoading]        = useState(false);
  const [submitError,    setSubmitError]    = useState('');
  const [activeTab,      setActiveTab]      = useState('return'); // 'return' | 'history'
  const [historyItems,   setHistoryItems]   = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const formBodyRef = useRef(null);

  // ── Seed form + history when modal opens ─────────────────────────────────
  useEffect(() => {
    if (!open || !asset) return;

    setReturnDate(getLocalDateString());
    setReturnRemarks('');
    setRemarksTouched(false);
    setSubmitError('');
    setActiveTab('return');
    setLoading(false);

    setHistoryLoading(true);
    try {
      const history = Array.isArray(asset.returnHistory)
        ? [...asset.returnHistory].reverse()
        : [];
      setHistoryItems(history);
    } catch (err) {
      console.error('[ReturnAssetModal] Failed parsing returns history', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [open, asset]);

  // ── Derived state ─────────────────────────────────────────────────────────
  const remarksMissing = returnRemarks.trim() === '';
  const canSubmit      = !loading && returnDate && !remarksMissing;
  const showRemarksError = remarksTouched && remarksMissing;

  const serialNumber    = asset?.manufacturerSerialNumber || '';
  const vendorName      =
    asset?.ownershipType === 'Leased'
      ? asset?.leaseVendorName
      : asset?.rentVendorName;
  const agreementNumber =
    asset?.ownershipType === 'Leased'
      ? asset?.leaseAgreementNumber
      : asset?.rentAgreementNumber;

  // ── Submit: Return Asset ──────────────────────────────────────────────────
  const handleReturn = useCallback(async () => {
    if (!canSubmit) return;

    setLoading(true);
    setSubmitError('');

    try {
      const updated = await assetService.returnAsset(
        asset.id, 
        {
          returnDate,
          returnRemarks: returnRemarks.trim(),
        }, 
        currentUser?.email
      );

      if (onSave)  onSave(updated);
      if (onError) onError('Asset returned successfully.', 'success');
      onClose();
    } catch (err) {
      console.error('Return failed:', err);
      setSubmitError(err.message || 'Failed to return asset. Please try again.');
      if (formBodyRef.current) {
        formBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setLoading(false);
    }
  }, [canSubmit, asset, returnDate, returnRemarks, currentUser, onSave, onError, onClose]);

  if (!asset) return null;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={!loading ? onClose : undefined}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          width: '100vw', height: '100vh',
          maxWidth: '100vw', maxHeight: '100vh',
          margin: 0, boxSizing: 'border-box',
        },
      }}
    >
      {/* ── APP BAR ──────────────────────────────────────────────────────── */}
      <AppBar sx={{ position: 'relative', flexShrink: 0 }} elevation={1}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} disabled={loading}>
            <CloseIcon />
          </IconButton>

          <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ lineHeight: 1.2 }}>
              Return Asset
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
              {asset.assetId} — {asset.assetName}
            </Typography>
          </Box>

          {/* Tab switcher */}
          <Box sx={{ display: 'flex', gap: 1, mr: 1 }}>
            {[
              { key: 'return',  label: 'Return',  icon: <AssignmentReturnIcon sx={{ fontSize: '1rem' }} /> },
              { key: 'history', label: 'History', icon: <HistoryIcon          sx={{ fontSize: '1rem' }} /> },
            ].map(({ key, label, icon }) => (
              <Button
                key={key}
                onClick={() => setActiveTab(key)}
                startIcon={icon}
                sx={{
                  color:       activeTab === key ? '#fff' : 'rgba(255,255,255,0.65)',
                  fontWeight:  activeTab === key ? 700   : 400,
                  textTransform: 'none', fontSize: '0.8125rem',
                  borderBottom: activeTab === key
                    ? '2px solid #fff'
                    : '2px solid transparent',
                  borderRadius: 0, px: 1.5,
                }}
              >
                {label}
                {key === 'history' && historyItems.length > 0 && (
                  <Chip
                    label={historyItems.length}
                    size="small"
                    sx={{
                      ml: 0.75, height: 18, fontSize: '0.65rem',
                      bgcolor: 'rgba(255,255,255,0.25)', color: '#fff',
                    }}
                  />
                )}
              </Button>
            ))}
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── SCROLLABLE BODY ───────────────────────────────────────────────── */}
      <Box
        ref={formBodyRef}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: '#f1f5f9', py: 4 }}
      >
        <Container maxWidth="md">
          <Stack spacing={3}>

            {/* ── Asset Summary ─────────────────────────────────────────── */}
            <SectionCard variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <SectionTitle>Asset Summary</SectionTitle>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>

                  <Box sx={{
                    flex: 1, minWidth: 220, p: 2, borderRadius: 2,
                    background: '#fff7ed', border: '1px solid #fdba74',
                  }}>
                    <Typography sx={{
                      fontSize: '0.75rem', fontWeight: 600, color: '#6b7280',
                      textTransform: 'uppercase', mb: 0.5,
                    }}>
                      Serial Number
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <QrCode2Icon sx={{ color: '#d97706', fontSize: '1.1rem' }} />
                      <Typography sx={{
                        fontSize: '1rem', fontWeight: 700, color: '#9a3412',
                        fontFamily: 'monospace',
                      }}>
                        {serialNumber || '—'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{
                    flex: 1, minWidth: 220, p: 2, borderRadius: 2,
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                  }}>
                    <Typography sx={{
                      fontSize: '0.75rem', fontWeight: 600, color: '#6b7280',
                      textTransform: 'uppercase', mb: 0.5,
                    }}>
                      Ownership
                    </Typography>
                    <Chip
                      label={asset.ownershipType || '—'}
                      size="small"
                      sx={{
                        fontWeight: 600, fontSize: '0.8125rem',
                        bgcolor: '#ede9fe', color: '#6d28d9',
                      }}
                    />
                    <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mt: 1 }}>
                      Location: {asset.locationName || asset.locationCode || '—'}
                    </Typography>
                  </Box>

                  <Box sx={{
                    flex: 1, minWidth: 220, p: 2, borderRadius: 2,
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                  }}>
                    <Typography sx={{
                      fontSize: '0.75rem', fontWeight: 600, color: '#6b7280',
                      textTransform: 'uppercase', mb: 0.5,
                    }}>
                      Vendor
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <BusinessIcon sx={{ color: '#6b7280', fontSize: '1rem' }} />
                      <Typography sx={{
                        fontSize: '0.9375rem', fontWeight: 700, color: '#1f2937',
                      }}>
                        {vendorName || '—'}
                      </Typography>
                    </Box>
                    {agreementNumber && (
                      <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.5 }}>
                        Agreement: {agreementNumber}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{
                    flex: 1, minWidth: 220, p: 2, borderRadius: 2,
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                  }}>
                    <Typography sx={{
                      fontSize: '0.75rem', fontWeight: 600, color: '#6b7280',
                      textTransform: 'uppercase', mb: 0.5,
                    }}>
                      Return History
                    </Typography>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>
                      {historyItems.length}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      {historyItems.length === 1 ? 'event recorded' : 'events recorded'}
                    </Typography>
                  </Box>

                </Box>
              </CardContent>
            </SectionCard>

            {/* ── TAB: RETURN ─────────────────────────────────────────── */}
            {activeTab === 'return' && (
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>Return Details</SectionTitle>

                  {/* Info banner */}
                  <Box sx={{
                    p: 2, mb: 2, borderRadius: 2,
                    background: '#fff7ed', border: '1px solid #fdba74',
                    display: 'flex', alignItems: 'center', gap: 1,
                  }}>
                    <AssignmentReturnIcon sx={{ color: '#d97706', fontSize: '1.1rem' }} />
                    <Typography sx={{ fontSize: '0.8125rem', color: '#9a3412' }}>
                      The asset will be marked as{' '}
                      <strong>Returned</strong>, removed from the active pool, and the
                      serial number will remain on record. This action will be recorded
                      in the return history.
                    </Typography>
                  </Box>

                  {/* Read-only serial */}
                  {serialNumber && (
                    <FormRow>
                      <FormGroup>
                        <FieldLabel>Manufacturer Serial Number</FieldLabel>
                        <TextField
                          fullWidth
                          size="small"
                          value={serialNumber}
                          InputProps={{ readOnly: true }}
                          disabled
                          sx={{
                            '& .MuiOutlinedInput-root': { background: '#f9fafb' },
                            '& input': {
                              fontFamily: 'monospace',
                              fontWeight: 600,
                              color: '#374151',
                            },
                          }}
                        />
                      </FormGroup>
                    </FormRow>
                  )}

                  {/* Return date + remarks */}
                  <FormRow>
                    <FormGroup>
                      <FieldLabel>
                        Return Date <span style={{ color: '#ef4444' }}>*</span>
                      </FieldLabel>
                      <TextField
                        type="date"
                        size="small"
                        fullWidth
                        value={returnDate}
                        onChange={(e) => setReturnDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: getLocalDateString() }} // Prevent UTC-shuffled blocking limits
                        required
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <FieldLabel>
                        Return Remarks <span style={{ color: '#ef4444' }}>*</span>
                      </FieldLabel>
                      <TextField
                        multiline
                        rows={4}
                        size="small"
                        fullWidth
                        value={returnRemarks}
                        onBlur={() => setRemarksTouched(true)} // Toggle error validation trigger on interaction
                        onChange={(e) => setReturnRemarks(e.target.value)}
                        placeholder="Condition on return, reason for return, any damages or missing accessories…"
                        inputProps={{ maxLength: 1000 }}
                        error={showRemarksError}
                        helperText={
                          showRemarksError
                            ? 'Remarks are required before submission'
                            : `${returnRemarks.length}/1000`
                        }
                        required
                      />
                    </FormGroup>
                  </FormRow>

                </CardContent>
              </SectionCard>
            )}

            {/* ── TAB: HISTORY ────────────────────────────────────────── */}
            {activeTab === 'history' && (
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>Return History</SectionTitle>

                  {historyLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={32} />
                    </Box>
                  )}

                  {!historyLoading && historyItems.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 6, color: '#6b7280' }}>
                      <HistoryIcon sx={{ fontSize: '3rem', opacity: 0.3, mb: 1 }} />
                      <Typography sx={{ fontWeight: 600 }}>
                        No return history yet
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', mt: 0.5 }}>
                        Return events will appear here once this asset is returned.
                      </Typography>
                    </Box>
                  )}

                  {!historyLoading && historyItems.length > 0 && (
                    <Stack spacing={2}>
                      {historyItems.map((entry, idx) => (
                        <HistoryEntry key={entry.id || idx}>
                          <Avatar
                            sx={{
                              width: 40, height: 40, flexShrink: 0,
                              bgcolor: '#fdba74', color: '#7c2d12',
                              fontSize: '0.875rem', fontWeight: 700,
                            }}
                          >
                            {getInitials(entry.returnedByName || entry.returnedBy || '')}
                          </Avatar>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{
                              display: 'flex', alignItems: 'center',
                              gap: 1, flexWrap: 'wrap', mb: 0.5,
                            }}>
                              <Chip
                                label="Returned"
                                size="small"
                                sx={{
                                  bgcolor: '#fff7ed', color: '#9a3412',
                                  fontWeight: 700, fontSize: '0.7rem',
                                  border: '1px solid #fdba74',
                                }}
                              />
                              <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                {formatDate(entry.returnDate)}
                              </Typography>
                              {idx === 0 && (
                                <Chip
                                  label="Latest" size="small"
                                  sx={{
                                    bgcolor: '#fef9c3', color: '#713f12',
                                    fontSize: '0.65rem', fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>

                            <Typography sx={{
                              fontWeight: 700, fontSize: '0.9375rem', color: '#1f2937',
                            }}>
                              Returned by: {entry.returnedByName || entry.returnedBy || '—'}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                Received by:{' '}
                                <strong>{entry.receivedBy || '—'}</strong>
                              </Typography>
                              {entry.returnedAt && (
                                <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  Logged:{' '}
                                  <strong>{formatDateTime(entry.returnedAt)}</strong>
                                </Typography>
                              )}
                            </Box>

                            {entry.returnRemarks && (
                              <Box sx={{
                                mt: 1, p: 1,
                                bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1,
                              }}>
                                <Typography sx={{
                                  fontSize: '0.8125rem', color: '#374151',
                                  fontStyle: 'italic',
                                }}>
                                  "{entry.returnRemarks}"
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </HistoryEntry>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </SectionCard>
            )}

          </Stack>
        </Container>
      </Box>

      {/* ── STICKY FOOTER ────────────────────────────────────────────────── */}
      <Box sx={{
        flexShrink: 0, width: '100%', height: '72px',
        borderTop: '1px solid #e2e8f0', backgroundColor: '#fff',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
        display: 'flex', alignItems: 'center',
        justifyContent: 'flex-end', gap: 1.5, px: 3,
      }}>
        <Button onClick={onClose} disabled={loading} sx={cancelBtnSx}>
          Cancel
        </Button>
        {activeTab === 'return' && (
          <Button onClick={handleReturn} disabled={!canSubmit} sx={submitBtnSx}>
            {loading && <CircularProgress size={16} color="inherit" />}
            {loading ? 'Processing…' : 'Confirm Return'}
          </Button>
        )}
      </Box>

      {/* ── ERROR SNACKBAR ────────────────────────────────────────────────── */}
      <Snackbar
        open={Boolean(submitError)}
        autoHideDuration={6000}
        onClose={() => setSubmitError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setSubmitError('')}
          severity="error"
          variant="filled"
          icon={<ErrorOutlineIcon />}
          sx={{ width: '100%', minWidth: 400, fontSize: '0.9rem', fontWeight: 500 }}
        >
          {submitError}
        </Alert>
      </Snackbar>

    </Dialog>
  );
};

export default ReturnAssetModal;